'use client';

import { FetchOrderDetailResponseBody } from 'bff';
import { CurrencySymbol, Prisma } from 'database';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { cn, DataTable } from 'ui';
import { formatTaxPercent } from 'utils';
import { useNumberFormatter } from '../hooks/useNumberFormatter';

type OrderItemRow = FetchOrderDetailResponseBody['items'][number];

// Los montos llegan como Prisma.Decimal en el tipo (string en runtime tras JSON).
type Amount = Prisma.Decimal | string | number | null;

// F2.5 — items table + totals of the order detail. Computes NOTHING: the server
// resolves the amounts, and the tax ones are `null` on older orders.
export const OrderItemsTable = ({
  items,
  totalAmount,
  coverageAmount,
  amountToCharge,
  taxesPercent,
  taxesAmount,
  totalWithTaxes,
  currency,
}: {
  items: OrderItemRow[];
  totalAmount: Amount;
  coverageAmount: Amount;
  amountToCharge: Amount;
  taxesPercent: Amount;
  taxesAmount: Amount;
  totalWithTaxes: Amount;
  currency: CurrencySymbol | null;
}) => {
  const { formatNumber } = useNumberFormatter(currency ?? undefined);

  const columns = useMemo(
    (): ColumnDef<OrderItemRow>[] => [
      {
        id: 'quantity',
        header: 'Cantidad',
        meta: { widthClassName: 'w-8', align: 'center' },
        cell: ({ row }) => (
          <span className='text-sm'>{row.original.quantity}</span>
        ),
      },
      {
        id: 'description',
        header: 'Descripción',
        // F2.5 — `description` is the only text: "SKU - Name (Brand)" for
        // catalog lines, or the user's text for manual ones.
        cell: ({ row }) => (
          <span className='text-sm'>{row.original.description}</span>
        ),
      },

      {
        id: 'unit_price',
        header: 'Precio unitario',
        meta: { align: 'right' },
        // A catalog line price is adjustable at creation: once it deviates, the
        // list price shows struck through, which is what `list_unit_price` is for.
        cell: ({ row }) => {
          const { unit_price, list_unit_price } = row.original;
          const isOverridden =
            list_unit_price != null &&
            Number(list_unit_price) !== Number(unit_price);

          return (
            <div className='flex flex-col items-end'>
              {isOverridden && (
                <span className='text-xs text-muted-foreground line-through'>
                  {formatNumber(Number(list_unit_price))}
                </span>
              )}
              <span className='text-sm'>
                {formatNumber(Number(unit_price))}
              </span>
            </div>
          );
        },
      },
      {
        id: 'subtotal',
        header: 'Importe',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className='text-sm font-medium'>
            {formatNumber(Number(row.original.subtotal))}
          </span>
        ),
      },
    ],
    [formatNumber],
  );

  if (items.length === 0) return null;

  return (
    <div className='flex flex-col gap-2'>
      <p className='text-sm font-semibold'>Detalle de la orden</p>
      <DataTable columns={columns} data={items} align='left' compact />
      <div className='flex flex-col items-end gap-0.5 pt-1 text-sm'>
        <div className='flex gap-6'>
          <span className='text-muted-foreground'>Importe</span>
          <span className='w-32 text-right font-medium'>
            {formatNumber(Number(totalAmount ?? 0))}
          </span>
        </div>
        <div className='flex gap-6'>
          <span className='text-muted-foreground'>Cobertura </span>
          <span className='w-32 text-right font-medium'>
            {coverageAmount
              ? `- ${formatNumber(Number(coverageAmount ?? 0))}`
              : 'No aplica'}
          </span>
        </div>
        <div className='flex gap-6'>
          <span className={taxesAmount == null ? 'font-semibold' : ''}>
            Importe neto
          </span>
          <span
            className={cn(
              'w-32 text-right',
              taxesAmount == null ? 'font-semibold' : 'font-medium',
            )}
          >
            {formatNumber(Number(amountToCharge ?? 0))}
          </span>
        </div>
        {taxesAmount != null && (
          <>
            <div className='flex gap-6'>
              <span className='text-muted-foreground'>
                IVA ({formatTaxPercent(Number(taxesPercent ?? 0))}%)
              </span>
              <span className='w-32 text-right font-medium'>
                {formatNumber(Number(taxesAmount))}
              </span>
            </div>
            <div className='flex gap-6'>
              <span className='font-semibold'>Total</span>
              <span className='w-32 text-right font-semibold'>
                {formatNumber(Number(totalWithTaxes ?? 0))}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
